import { definePreset } from "@primeuix/themes";
import Aura from "@primeuix/themes/aura";

/* Ink as a primary scale: white-to-black tints for 50-400, black from 500 up. */
const ink = {
  50: "#f2f2f2",
  100: "#e6e6e6",
  200: "#cccccc",
  300: "#b3b3b3",
  400: "#999999",
  500: "#000000",
  600: "#000000",
  700: "#000000",
  800: "#000000",
  900: "#000000",
  950: "#000000",
};

/* Tickable v9 "Knipkaart" field colours, registered as PrimeVue
   primitives so components and pages can reach them by token
   (`--p-signal`, `{ink}`) instead of pasting hexes. The same values are
   registered as Tailwind colours in tailwind.config.js; this is the
   other half of the same palette, for the parts PrimeVue paints.
   Source of truth: D:\Projects\tickable-nl\DESIGN.md §2. */
const knipkaart = {
  ink: "#131313",
  ink2: "#3D3D3D",
  ink3: "#656563",
  ink5: "#949494",
  ash: "#EFEFED",
  ash2: "#E4E4E1",
  signal: "#F2F665",
  signalDeep: "#E2E650",
  ice: "#BFF0FF",
  mint: "#B2E898",
  lilac: "#DABCFF",
  plum: "#67003C",
  petrol: "#004760",
  ultra: "#2A119B",
  rose: "#FF1D5E",
  pine: "#176B3C",
};

/* Status colours on v9's fields (DESIGN.md §2): a message, toast, tag or
   badge is a flat field with its own foreground, never a tint with
   coloured text. Success mint, info ice, warning signal, error the plum
   plate, neutral ash. `text` is the colour for a text-only message on
   white (yellow text on white is 1.4:1 and never ships), `textDark` the
   same on the dark ground, where the light fields read as they are and
   rose is the error state. */
type Status = { field: string; ink: string; text: string; textDark: string };
const status: Record<"success" | "info" | "warn" | "error" | "secondary" | "contrast", Status> = {
  success: { field: "{mint}", ink: "{ink}", text: "{pine}", textDark: "{mint}" },
  info: { field: "{ice}", ink: "{ink}", text: "{petrol}", textDark: "{ice}" },
  warn: { field: "{signal}", ink: "{ink}", text: "{ink2}", textDark: "{signal}" },
  error: { field: "{plum}", ink: "#ffffff", text: "{plum}", textDark: "{rose}" },
  secondary: { field: "{ash}", ink: "{ink}", text: "{ink3}", textDark: "{surface.400}" },
  contrast: { field: "{ink}", ink: "#ffffff", text: "{ink}", textDark: "{surface.0}" },
};
/* On the dark ground the ink plate disappears; it turns to paper there. */
const statusDark = { ...status, contrast: { field: "#ffffff", ink: "{ink}", text: "{ink}", textDark: "{surface.0}" } };

/** The plate's own foreground at low alpha, never a grey (DESIGN.md "the rules that bind"). */
const onField = (tone: Status) => (tone.ink === "#ffffff" ? "rgba(255, 255, 255, 0.16)" : "rgba(19, 19, 19, 0.1)");

const bySeverity = <T,>(tones: Record<string, Status>, build: (tone: Status, dark: boolean) => T, dark: boolean) =>
  Object.fromEntries(Object.entries(tones).map(([name, tone]) => [name, build(tone, dark)]));

/* A notice in the page (Message) is lighter than a label: on light it is a pale field in its
   tone with dark tone text, as in the design sheets; the full plates stay for Tag, Badge and
   Toast. Warn is already a light field. Dark keeps the plates. */
const notice: Record<keyof typeof status, Pick<Status, "field" | "ink">> = {
  success: { field: "#E3F6D8", ink: "#0F4D2A" },
  info: { field: "#DDEFF6", ink: "{petrol}" },
  warn: { field: "{signal}", ink: "{ink}" },
  error: { field: "#F6E3EC", ink: "#4A0029" },
  secondary: { field: "{ash}", ink: "{ink2}" },
  contrast: status.contrast,
};
const noticeLight = Object.fromEntries(
  Object.entries(status).map(([name, tone]) => [name, { ...tone, ...notice[name as keyof typeof status] }]),
) as typeof status;

const message = (tone: Status, dark: boolean) => ({
  background: tone.field,
  borderColor: tone.field,
  color: tone.ink,
  shadow: "none",
  closeButton: { hoverBackground: onField(tone), focusRing: { color: tone.ink, shadow: "none" } },
  outlined: { color: dark ? tone.textDark : tone.text, borderColor: dark ? tone.textDark : tone.text },
  simple: { color: dark ? tone.textDark : tone.text },
});

const toast = (tone: Status) => ({
  background: tone.field,
  borderColor: tone.field,
  color: tone.ink,
  detailColor: tone.ink,
  // A toast floats over the page, so it keeps a soft drop; plates in the page don't.
  shadow: "0 8px 24px rgba(19, 19, 19, 0.14)",
  closeButton: { hoverBackground: onField(tone), focusRing: { color: tone.ink, shadow: "none" } },
});

const chip = (tone: Status) => ({ background: tone.field, color: tone.ink });
/* Tag and Badge call the error severity "danger". */
const chips = (tones: Record<string, Status>) => {
  const { error, ...rest } = bySeverity(tones, chip, false) as Record<string, { background: string; color: string }>;
  return { ...rest, danger: error };
};

export const MyPreset = definePreset(Aura, {
  primitive: knipkaart,
  semantic: {
    /* The action is ink. v9 says so and this preset already said so;
       it is the one part of the dashboard that was v9 before v9. */
    primary: ink,
    /* v9's focus ring: a 2px ultramarine ring on light, swapping to
       signal on dark where ultramarine sinks into the ground. Aura's
       default draws the ring in the primary colour, which on this
       preset is black on near-black — nearly invisible in dark mode
       and indistinguishable from a border in light. */
    focusRing: {
      width: "2px",
      style: "solid",
      color: "{ultra}",
      offset: "2px",
      shadow: "none",
    },
    colorScheme: {
      light: {
        /* Selected things (an option in a dropdown, a selected row, the
           active choice in a SelectButton) sit on the lilac field with ink
           on it: a flat v9 field, and the dashboard's one accent colour. */
        highlight: {
          background: "{lilac}",
          focusBackground: "#CBA3FF",
          color: "{ink}",
          focusColor: "{ink}",
        },
        /* The neutral surface-500 (ink3) passes 4.5:1 on a filled field, unlike Aura's stone-500. */
        formField: {
          placeholderColor: "{surface.500}",
        },
        /* Body text is ink like the design sheets, not Aura's warm stone-700; muted text is the
           neutral ink3 (5:1 on the ash ground) instead of stone-500 (4.2:1). */
        text: {
          color: "{ink}",
          hoverColor: "{ink}",
          mutedColor: "{ink3}",
          hoverMutedColor: "{ink2}",
        },
        /* Neutral greys from the design sheets instead of Aura's warm stone, so explicit
           text-surface-* classes match the inks. Roles: 0 card, 50 inner field/hover,
           100 the ash ground, 200 hairlines (ash2), 300 strong borders, 400 faint meta,
           500 muted body text (ink3, 5:1 on ash), 600 stronger muted, 700 body (ink2),
           900 headings (ink). */
        surface: {
          0: '#ffffff',
          50: '#F7F7F5',
          100: '#EFEFED',
          200: '#E4E4E1',
          300: '#CFCFCB',
          400: '#8A8A87',
          500: '#656563',
          600: '#4D4D4B',
          700: '#3D3D3D',
          800: '#262626',
          900: '#131313',
          950: '#0A0A0A'
        }
      },
      dark: {
        focusRing: {
          color: "{signal}",
        },
        highlight: {
          background: "{lilac}",
          focusBackground: "#CBA3FF",
          color: "{ink}",
          focusColor: "{ink}",
        },
        primary: {
          0: '{stone.100}',
          50: '{stone.100}',
          100: '{stone.100}',
          200: '{stone.100}',
          300: '{stone.100}',
          400: '{stone.100}',
          500: '{stone.100}',
          600: '{stone.100}',
          700: '{stone.100}',
          800: '{stone.100}',
          900: '{stone.100}',
          950: '{stone.50}'
        },
        /* Dark mode in v9's neutral greys, like the sign-in screen: the ground is ink (#131313,
           surface-950), cards a clear step up (#1F1F1F, surface-900), fields and borders above
           that. The warm stone scale read brown on the ink ground. */
        surface: {
          0: '#ffffff',
          50: '#fafafa',
          100: '#f4f4f3',
          200: '#e4e4e2',
          300: '#cfcfcc',
          400: '#a3a3a0',
          500: '#8a8a87',
          600: '#5f5f5d',
          700: '#3d3d3c',
          800: '#2b2b2a',
          900: '#1f1f1f',
          950: '#131313'
        }
      }
    }
  },
  components: {
    message: {
      // A notice reads at body size (14px), like the sheets; Aura's 16px/medium made it shout
      content: { padding: "0.5rem 0.75rem", gap: "0.5rem" },
      text: { fontSize: "0.875rem", fontWeight: "400" },
      icon: { size: "1rem" },
      colorScheme: {
        light: bySeverity(noticeLight, message, false),
        dark: bySeverity(statusDark, message, true),
      },
    },
    toast: {
      colorScheme: {
        light: bySeverity(status, toast, false),
        dark: bySeverity(statusDark, toast, true),
      },
    },
    /* The destructive button is the error plate: plum, and rose on the dark
       ground where plum sinks into it (rose carries ink: white on rose fails AA). */
    button: {
      /* Every button is a pill (v9's one round shape: the thing you press).
         Icon-only buttons become circles. */
      root: {
        borderRadius: "9999px",
      },
      colorScheme: {
        light: {
          root: {
            /* Secondary: ink on ash2, so it reads as a button and not as disabled on the ash ground
               (Aura's grey text on surface-100 did) and still shows on white cards. */
            secondary: {
              background: "{ash2}", hoverBackground: "#D9D9D5", activeBackground: "#CFCFCA",
              borderColor: "{ash2}", hoverBorderColor: "#D9D9D5", activeBorderColor: "#CFCFCA",
              color: "{ink}", hoverColor: "{ink}", activeColor: "{ink}",
              focusRing: { color: "{ink}", shadow: "none" },
            },
            /* The positive action (Inchecken): the mint plate with ink, as the success status. */
            success: {
              background: "{mint}", hoverBackground: "#9FDC83", activeBackground: "#8ED170",
              borderColor: "{mint}", hoverBorderColor: "#9FDC83", activeBorderColor: "#8ED170",
              color: "{ink}", hoverColor: "{ink}", activeColor: "{ink}",
              focusRing: { color: "{ink}", shadow: "none" },
            },
            danger: {
              background: "{plum}", hoverBackground: "#4F002E", activeBackground: "#3B0022",
              borderColor: "{plum}", hoverBorderColor: "#4F002E", activeBorderColor: "#3B0022",
              color: "#ffffff", hoverColor: "#ffffff", activeColor: "#ffffff",
              focusRing: { color: "{plum}", shadow: "none" },
            },
          },
          outlined: { danger: { hoverBackground: "rgba(103, 0, 60, 0.06)", activeBackground: "rgba(103, 0, 60, 0.12)", borderColor: "{plum}", color: "{plum}" } },
          text: { danger: { hoverBackground: "rgba(103, 0, 60, 0.06)", activeBackground: "rgba(103, 0, 60, 0.12)", color: "{plum}" },
            secondary: { hoverBackground: "rgba(19, 19, 19, 0.06)", activeBackground: "rgba(19, 19, 19, 0.1)", color: "{ink2}" },
            success: { hoverBackground: "rgba(178, 232, 152, 0.25)", activeBackground: "rgba(178, 232, 152, 0.4)", color: "{pine}" } },
        },
        dark: {
          root: {
            secondary: {
              background: "{surface.800}", hoverBackground: "{surface.700}", activeBackground: "{surface.600}",
              borderColor: "{surface.800}", hoverBorderColor: "{surface.700}", activeBorderColor: "{surface.600}",
              color: "{surface.0}", hoverColor: "{surface.0}", activeColor: "{surface.0}",
              focusRing: { color: "{surface.0}", shadow: "none" },
            },
            /* The positive action (Inchecken): the mint plate with ink, as the success status. */
            success: {
              background: "{mint}", hoverBackground: "#9FDC83", activeBackground: "#8ED170",
              borderColor: "{mint}", hoverBorderColor: "#9FDC83", activeBorderColor: "#8ED170",
              color: "{ink}", hoverColor: "{ink}", activeColor: "{ink}",
              focusRing: { color: "{mint}", shadow: "none" },
            },
            danger: {
              background: "{rose}", hoverBackground: "#FF4A7E", activeBackground: "#FF6F97",
              borderColor: "{rose}", hoverBorderColor: "#FF4A7E", activeBorderColor: "#FF6F97",
              color: "{ink}", hoverColor: "{ink}", activeColor: "{ink}",
              focusRing: { color: "{rose}", shadow: "none" },
            },
          },
          outlined: { danger: { hoverBackground: "rgba(255, 29, 94, 0.08)", activeBackground: "rgba(255, 29, 94, 0.16)", borderColor: "{rose}", color: "{rose}" } },
          text: { danger: { hoverBackground: "rgba(255, 29, 94, 0.08)", activeBackground: "rgba(255, 29, 94, 0.16)", color: "{rose}" },
            secondary: { hoverBackground: "rgba(255, 255, 255, 0.06)", activeBackground: "rgba(255, 255, 255, 0.1)", color: "{surface.200}" },
            success: { hoverBackground: "rgba(178, 232, 152, 0.1)", activeBackground: "rgba(178, 232, 152, 0.2)", color: "{mint}" } },
        },
      },
    },
    /* SelectButton (e.g. upcoming/past events): a pill track on the ash
       field with the chosen option as the ink pill, like the current place
       in the sidebar; inverted on the dark ground. */
    selectbutton: { root: { borderRadius: "9999px" } },
    togglebutton: {
      root: { borderRadius: "9999px" },
      content: { borderRadius: "9999px", checkedShadow: "none" },
      colorScheme: {
        light: {
          root: {
            // ash2, a step darker than the ash page ground, so the track shows there and on white cards
            background: "{ash2}", checkedBackground: "{ash2}", hoverBackground: "{ash2}",
            borderColor: "{ash2}", checkedBorderColor: "{ash2}",
            color: "{ink3}", hoverColor: "{ink}", checkedColor: "{ink}",
          },
          // A filter, not an action: the selection is a white pill on the ash track, not ink.
          content: { checkedBackground: "#ffffff" },
          icon: { color: "{ink3}", hoverColor: "{ink}", checkedColor: "{ink}" },
        },
        dark: {
          root: {
            background: "{surface.800}", checkedBackground: "{surface.800}", hoverBackground: "{surface.800}",
            borderColor: "{surface.800}", checkedBorderColor: "{surface.800}",
            color: "{surface.400}", hoverColor: "{surface.100}", checkedColor: "#ffffff",
          },
          content: { checkedBackground: "{surface.600}" },
          icon: { color: "{surface.400}", hoverColor: "{surface.100}", checkedColor: "#ffffff" },
        },
      },
    },
    /* Table headers are transparent (main.css), so a sorted column keeps the normal text
       colour; the highlight's ink vanished on the dark ground. The sort icon shows the order. */
    datatable: { headerCell: { selectedBackground: "transparent", selectedColor: "{content.color}" } },
    tag: { colorScheme: { light: chips(status), dark: chips(statusDark) } },
    badge: { colorScheme: { light: chips(status), dark: chips(statusDark) } },
  },
});
//Your customizations, see the following sections for examples

export default {
  preset: MyPreset,
  options: {
    darkModeSelector: ".p-dark",
    cssLayer: false
  },
};
